import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {
  BokkzAlgDataService, BokkzClient,
  BokkzMumaClient,
  BokkzMumaDataService,
  ClientEntity,
  MumaEntity, MumaEntityMachtigingStatus, MumaOverzicht, MumaRequestDienst
} from "@snelstart-ventures/fiscaal/domain-logic";
import {ActivatedRoute} from "@angular/router";
import {FormBuilder} from "@angular/forms";
import {ToastService} from "@snelstart/ui-toast";
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {SearchCriteria, SearchResult} from "@snelstart/domain-logic";
import {delay, map, Observable, of, Subject, switchMap, takeUntil} from "rxjs";
import {CommonModule} from '@angular/common';
import {environment} from "../../../../../../../apps/ventures/fiscaal/src/environments/environment";
import {SharedUiAlertModule} from "@snelstart/ui-alert";
import {SharedUiLoadingModule} from "@snelstart/ui-loading";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-muma-list',
  templateUrl: './feature-tax-muma-list.component.html',
  styleUrl: './feature-tax-muma-list.component.scss',
  imports: [
    SharedUiTableModule,
    CommonModule,
    SharedUiAlertModule,
    SharedUiLoadingModule
  ]
})
export class FeatureTaxMumaListComponent implements OnInit, OnDestroy {

  @Output() public details = new EventEmitter<any>();
  protected readonly MumaEntityMachtigingStatus = MumaEntityMachtigingStatus;
  protected tableConfig!: TableConfiguration<SearchCriteria, MumaEntity>;
  protected tableConfigOverzicht!: TableConfiguration<SearchCriteria, MumaOverzicht>;
  protected taxId: any;
  protected clientId: any;
  private destroy$ = new Subject<void>();
  public clientEntity = {} as ClientEntity;
  public mumaOverzicht: Observable<MumaOverzicht[]> = of([]);
  public dialogVisible = false;
  public year = new Date().getFullYear() - 1;
  public showTable = false;
  public showTableOverzicht = false;
  public tableLoading = true;

  constructor(
    public dataService: BokkzMumaDataService,
    public algDataService: BokkzAlgDataService,
    public bokkzMumaClient: BokkzMumaClient,
    public bokkzClient: BokkzClient,
    public toastService: ToastService,
    public fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.clientId = params['clientId'];
      if (params['clientId'] === "0") {
        this.bokkzMumaClient.getMumaOverzicht().pipe(takeUntil(this.destroy$)).subscribe(res => {
          if (res.length > 0) {
            this.mumaOverzicht = of(res).pipe(delay(50));
            setTimeout(() => {
              this.showTableOverzicht = true;
              this.tableLoading = false;
              this.createTableConfigOverzicht();
            }, 0);
          }
        })
      } else {
        this.bokkzClient.getClient(this.clientId).pipe(takeUntil(this.destroy$)).subscribe((value) => {
          this.algDataService.updateClient(value);
          this.clientEntity = value;
          this.taxId = value.taxId;
          setTimeout(() => {
            this.createTableConfig();
            this.tableLoading = false;
            this.showTable = true;
          }, 0);
        });
      }
    });
    this.algDataService.client.subscribe((value) => {
      this.clientEntity = value;
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public createTableConfigOverzicht(): void {
    this.tableConfigOverzicht = TableConfiguration.create<SearchCriteria, MumaOverzicht>({
      identifier: 'id',
      options: {
        useSearch: true,
        useSort: true,
      },
      criteria: {take: 25, skip: 0, sortDescending: false},
      search: (criteria: SearchCriteria): Observable<SearchResult<MumaOverzicht>> => {
        return this.mumaOverzicht.pipe(
          delay(50),
          map((mumaList: MumaOverzicht[]) => { // type explicitly here
            let filteredList = [...mumaList];

            // --- Apply search filter ---
            if (criteria.searchTerm?.trim()) {
              const searchLower = criteria.searchTerm.toLowerCase().trim();
              filteredList = filteredList.filter(item =>
                item.clientName?.toLowerCase().includes(searchLower)
              );
            }

            // --- Apply paging ---
            const start = criteria.skip ?? 0;
            const end = start + (criteria.take ?? filteredList.length);
            const pageItems = filteredList.slice(start, end);

            return new SearchResult<MumaOverzicht>(pageItems, filteredList.length); // <MumaOverzicht> is required
          }),
        );
      },
      details: (entity) => {
        if (entity.sstRelationId != undefined) {
          window.open(environment.fiscaalBaseUrl + 'tax-muma/' + entity.sstRelationId);
        }
      },
      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: $localize`Geen machtigingen aanwezig`,
        subText: 'Het aanvragen van een machtiging kun je doen bij de belastingplichtige (via het klantenoverzicht).',
      }),
    });
  }

  public createTableConfig(): void {
    this.tableConfig = TableConfiguration.create<SearchCriteria, MumaEntity>({
      identifier: 'id',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria: {take: 6, skip: 2, sortDescending: false} as SearchCriteria,
      search: () => {
        const mumaList = this.bokkzMumaClient.getAllMuma(this.clientId);
        return mumaList.pipe(
          switchMap((mumaList) => {
            const result = mumaList.sort((a, b) =>
              this.getTypeName(a.type!).localeCompare(this.getTypeName(b.type!))
            );
            this.dataService.updateMumaList(result)
            return new Observable<SearchResult<MumaEntity>>((subscriber) => {
              subscriber.next(new SearchResult(result, result.length));
              subscriber.complete();
            });
          }),
        );
      },
      details: (entity) => {
        if (entity.id != undefined) {
          this.dataService.updateMumaEntity(entity);
          this.details.next(entity.id);
        }
      },
      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: $localize`Geen machtigingen aanwezig`,
        subText: 'Klik op  \' + Nieuwe machtiging\' voor het aanvragen van een machtiging.',
      }),
    });
  }

  getTypeName(type: string): string {
    const typeNameMap: Record<string, string> = {
      'SBA_VpB_2024': 'Machtiging servicebericht aanslag vennootschapsbelasting 2024',
      'SBA_VpB_2025': 'Machtiging servicebericht aanslag vennootschapsbelasting 2025',
      'SBA_VpB_2026': 'Machtiging servicebericht aanslag vennootschapsbelasting 2026',
      'SBA_IH_2024': 'Machtiging servicebericht aanslag inkomstenbelasting 2024',
      'SBA_IH_2025': 'Machtiging servicebericht aanslag inkomstenbelasting 2025',
      'SBA_IH_2026': 'Machtiging servicebericht aanslag inkomstenbelasting 2026',
      'SBA_OB_2024': 'Machtiging servicebericht aanslag omzetbelasting 2024',
      'SBA_OB_2025': 'Machtiging servicebericht aanslag omzetbelasting 2025',
      'SBA_OB_2026': 'Machtiging servicebericht aanslag omzetbelasting 2026',
      'SBA_TSG_2024': 'Machtiging servicebericht toeslagen 2024',
      'SBA_TSG_2025': 'Machtiging servicebericht toeslagen 2025',
      'SBA_TSG_2026': 'Machtiging servicebericht toeslagen 2026',
      'VIA_2024': 'Machtiging gegevens vooraf ingevulde aangifte 2024',
      'VIA_2025': 'Machtiging gegevens vooraf ingevulde aangifte 2025',
      'VIA_2026': 'Machtiging gegevens vooraf ingevulde aangifte 2026',
      'DoMa_IH': 'Doorlopende machtiging inkomstenbelasting',
      'DoMa_OB': 'Doorlopende machtiging omzetbelasting',
      'DoMa_VpB': 'Doorlopende machtiging vennootschapsbelasting',
      'DoMa_TSG': 'Doorlopende machtiging toeslagen',
    };
    return typeNameMap[type]; // Geef de naam terug, of het type zelf als de naam niet gevonden is
  }

  public checkIsOptInMachtiging(dienst?: MumaRequestDienst) {
    return dienst?.toString().includes('SBA_TSG')
      || dienst?.toString().includes('VIA')
      || dienst?.toString().includes('DoMa')
  }

}
